/**
 * TimeWheelSelector Component
 *
 * Scrollable hour/minute wheels for precise time selection
 * Uses CSS scroll-snap for smooth scrolling behavior
 */

import { useEffect, useRef, useMemo, useCallback, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { TimeWheelSelectorProps } from './types';

export const TimeWheelSelector = ({
  selectedTime,
  onTimeChange,
  disabled = false,
}: TimeWheelSelectorProps) => {
  const { t } = useTranslation();
  const hoursRef = useRef<HTMLDivElement>(null);
  const minutesRef = useRef<HTMLDivElement>(null);

  // Parse selected time
  const [selectedHour, selectedMinute] = useMemo(() => {
    const [h, m] = selectedTime.split(':').map(Number);
    return [h, m];
  }, [selectedTime]);

  // Generate hours (00-23)
  const hours = useMemo(() => Array.from({ length: 24 }, (_, i) => i), []);

  // Generate minutes in 5-minute increments (00, 05, 10, ..., 55)
  const minutes = useMemo(() => Array.from({ length: 12 }, (_, i) => i * 5), []);

  // Scroll to selected value on mount or when selected time changes externally
  useEffect(() => {
    if (hoursRef.current && minutesRef.current) {
      const hourElement = hoursRef.current.querySelector(
        `[data-value="${selectedHour}"]`
      ) as HTMLElement;
      const minuteElement = minutesRef.current.querySelector(
        `[data-value="${selectedMinute}"]`
      ) as HTMLElement;

      if (hourElement) {
        hourElement.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
      if (minuteElement) {
        minuteElement.scrollIntoView({ block: 'center', behavior: 'auto' });
      }
    }
  }, []);

  // Handle hour/minute selection
  const handleHourClick = useCallback(
    (hour: number) => {
      if (disabled) return;
      const newTime = `${hour.toString().padStart(2, '0')}:${selectedMinute.toString().padStart(2, '0')}`;
      onTimeChange(newTime);
    },
    [selectedMinute, onTimeChange, disabled]
  );

  const handleMinuteClick = useCallback(
    (minute: number) => {
      if (disabled) return;
      const newTime = `${selectedHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      onTimeChange(newTime);
    },
    [selectedHour, onTimeChange, disabled]
  );

  // Keyboard navigation for hours
  const handleHourKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      let newHour = selectedHour;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          newHour = selectedHour > 0 ? selectedHour - 1 : 23;
          break;
        case 'ArrowDown':
          e.preventDefault();
          newHour = selectedHour < 23 ? selectedHour + 1 : 0;
          break;
        default:
          return;
      }

      handleHourClick(newHour);

      // Scroll to new hour
      const hourElement = hoursRef.current?.querySelector(
        `[data-value="${newHour}"]`
      ) as HTMLElement;
      if (hourElement) {
        hourElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    },
    [selectedHour, handleHourClick, disabled]
  );

  // Keyboard navigation for minutes
  const handleMinuteKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      const currentIndex = minutes.indexOf(selectedMinute);
      let newIndex = currentIndex;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          newIndex = currentIndex > 0 ? currentIndex - 1 : minutes.length - 1;
          break;
        case 'ArrowDown':
          e.preventDefault();
          newIndex = currentIndex < minutes.length - 1 ? currentIndex + 1 : 0;
          break;
        default:
          return;
      }

      const newMinute = minutes[newIndex];
      handleMinuteClick(newMinute);

      // Scroll to new minute
      const minuteElement = minutesRef.current?.querySelector(
        `[data-value="${newMinute}"]`
      ) as HTMLElement;
      if (minuteElement) {
        minuteElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    },
    [selectedMinute, minutes, handleMinuteClick, disabled]
  );

  // Custom wheel event handler for controlled scroll speed (one item per tick)
  const handleWheel = useCallback(
    (e: WheelEvent, type: 'hour' | 'minute') => {
      e.preventDefault();
      const delta = Math.sign(e.deltaY); // Just get direction, ignore magnitude

      if (type === 'hour') {
        const newHour = (selectedHour + delta + 24) % 24;
        handleHourClick(newHour);

        // Scroll to the new hour
        const hourElement = hoursRef.current?.querySelector(
          `[data-value="${newHour}"]`
        ) as HTMLElement;
        if (hourElement) {
          hourElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      } else {
        const currentIndex = minutes.indexOf(selectedMinute);
        const newIndex = (currentIndex + delta + minutes.length) % minutes.length;
        const newMinute = minutes[newIndex];
        handleMinuteClick(newMinute);

        // Scroll to the new minute
        const minuteElement = minutesRef.current?.querySelector(
          `[data-value="${newMinute}"]`
        ) as HTMLElement;
        if (minuteElement) {
          minuteElement.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }
    },
    [selectedHour, selectedMinute, minutes, handleHourClick, handleMinuteClick]
  );

  // Attach wheel event listeners for smooth scrolling
  useEffect(() => {
    const hoursEl = hoursRef.current;
    const minutesEl = minutesRef.current;

    const onHoursWheel = (e: WheelEvent) => handleWheel(e, 'hour');
    const onMinutesWheel = (e: WheelEvent) => handleWheel(e, 'minute');

    hoursEl?.addEventListener('wheel', onHoursWheel, { passive: false });
    minutesEl?.addEventListener('wheel', onMinutesWheel, { passive: false });

    return () => {
      hoursEl?.removeEventListener('wheel', onHoursWheel);
      minutesEl?.removeEventListener('wheel', onMinutesWheel);
    };
  }, [handleWheel]);

  // Format number with leading zero
  const formatNumber = useCallback(
    (num: number) => num.toString().padStart(2, '0'),
    []
  );

  return (
    <div>
      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
        {t('time_exact')}
      </h4>

      <div className="flex items-center gap-2">
        {/* Hours Wheel */}
        <div className="flex-1">
          <div
            ref={hoursRef}
            className={cn(
              'h-[90px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100',
              'snap-y snap-mandatory rounded-lg border border-gray-200 bg-gray-50',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            style={{ scrollBehavior: 'smooth', overscrollBehavior: 'contain' }}
            onKeyDown={handleHourKeyDown}
            tabIndex={disabled ? -1 : 0}
            role="listbox"
            aria-label={t('select_hour', { defaultValue: 'Select hour' })}
            aria-activedescendant={`hour-${selectedHour}`}
          >
            {hours.map((hour) => (
              <div
                key={hour}
                id={`hour-${hour}`}
                data-value={hour}
                onClick={() => handleHourClick(hour)}
                className={cn(
                  'h-[30px] flex items-center justify-center snap-center cursor-pointer transition-all duration-150 text-xs',
                  hour === selectedHour
                    ? 'bg-primary-500 text-primary-contrast font-semibold'
                    : 'hover:bg-gray-200 text-gray-700',
                  disabled && 'pointer-events-none'
                )}
                role="option"
                aria-selected={hour === selectedHour}
              >
                {formatNumber(hour)}
              </div>
            ))}
          </div>
        </div>

        {/* Separator */}
        <div className="text-xl font-bold text-gray-400">:</div>

        {/* Minutes Wheel */}
        <div className="flex-1">
          <div
            ref={minutesRef}
            className={cn(
              'h-[90px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100',
              'snap-y snap-mandatory rounded-lg border border-gray-200 bg-gray-50',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            style={{ scrollBehavior: 'smooth', overscrollBehavior: 'contain' }}
            onKeyDown={handleMinuteKeyDown}
            tabIndex={disabled ? -1 : 0}
            role="listbox"
            aria-label={t('select_minute', { defaultValue: 'Select minute' })}
            aria-activedescendant={`minute-${selectedMinute}`}
          >
            {minutes.map((minute) => (
              <div
                key={minute}
                id={`minute-${minute}`}
                data-value={minute}
                onClick={() => handleMinuteClick(minute)}
                className={cn(
                  'h-[30px] flex items-center justify-center snap-center cursor-pointer transition-all duration-150 text-xs',
                  minute === selectedMinute
                    ? 'bg-primary-500 text-primary-contrast font-semibold'
                    : 'hover:bg-gray-200 text-gray-700',
                  disabled && 'pointer-events-none'
                )}
                role="option"
                aria-selected={minute === selectedMinute}
              >
                {formatNumber(minute)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
